import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import * as moment from 'moment-timezone';
import { NgxSmartModalService } from 'ngx-smart-modal';
import {
  AlertService,
  CampaignService,
  FunnelService,
  LoaderService,
  PusherService,
  SingleStepFilterService,
  StepTemplateService,
  UserService,
} from '../../_services';
import { CrudPagedListComponent } from '../../_directives';
import {
  Pager,
  OfferIntents,
  OfferTypes,
  StepTableStatusLabels,
  StepTableStatuses,
  User,
  StepsSortConfig,
  TableSortDirection,
  StepSortableColumns,
  StepHeaderDetails,
  RetentionTooltips,
  Campaign, NotificationType, FunnelType,
  AudiencePopup,
  SingleStepPathFilter,
  getAudienceConditions,
} from '../../_models';
import { FirstStep, SingleStep } from '../retention/retention';
import {takeUntil} from 'rxjs/operators';

@Component({
  moduleId: module.id.toString(),
  selector: 'single-step-path-component',
  templateUrl: './single-step-path.component.html',
  styleUrls: ['./single-step-path.component.scss'],
})
export class SingleStepPathComponent extends CrudPagedListComponent implements OnInit {
  user: User;
  campaign: Campaign;
  tableStatuses = StepTableStatuses;
  tableData = [];
  templates = [];
  originalTableData = [...this.tableData];
  offerType: OfferTypes = OfferTypes.Downsell;
  isLoading = false;
  offerIntents = OfferIntents;
  stepStatusLabel = StepTableStatusLabels;
  stepHeaderDetails = StepHeaderDetails;
  retentionTooltips = RetentionTooltips;
  currentSort: StepsSortConfig = {
    column: null,
    direction: null,
  };
  currentTemplateSort: StepsSortConfig = {
    column: null,
    direction: null,
  };
  isAdminView: boolean = false;
  columnDirections = TableSortDirection;
  sortableColumns = StepSortableColumns;
  public offerTypeOptions: { value: string; label: string }[] = [];
  campaignFunnels: (string | number)[] = []
  // filter rows for the brand currently selected, keyed by path id
  pathFilters: { [key: number]: SingleStepPathFilter } = {};
  popupTitle = '';
  popupData: any = {
    campaign_id: null,
    funnel_id: null,
    filter_id: null,
    audience_id: null,
    popupType: AudiencePopup.Conditions,
  };

  constructor(
    protected router: Router,
    protected location: Location,
    protected alertService: AlertService,
    protected funnelService: FunnelService,
    protected stepService: StepTemplateService,
    protected campaignService: CampaignService,
    protected userService: UserService,
    protected loader: LoaderService,
    public ngxSmartModalService: NgxSmartModalService,
    protected route: ActivatedRoute,
    protected pusherService: PusherService,
    protected singleStepFilterService: SingleStepFilterService
  ) {
    super(router, location, route, funnelService, alertService);
    this.objectName = 'single step';
  }

  ngOnInit() {
    this.loader.show();
    this.campaign = this.campaignService.getCurrentCampaign();
    this.processAssignedFunnels(this.campaign);

    this.userService.getCurrent().subscribe((user: User) => {
      this.user = user;
    });

    this.fetchOfferData();

    if (this.pusherService.channel) {
      this.pusherService.channel.bind(NotificationType[NotificationType.COPY_FUNNEL], (data: any) => {
        data = data.payload;
        if (data && data.is_completed) {
          this.fetchOfferData();
        }
      });
    }

    this.campaignService.currentCampaignUpdate$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((campaign: Campaign) => {
        this.processAssignedFunnels(campaign);
      })
  }

  processAssignedFunnels(campaign: Campaign) {
    if (campaign) {
      this.campaign = campaign;
      this.campaignFunnels = this.campaign.single_step_funnels;
      this.fetchPathFilters();
    }
  }

  /**
   * Display filters are per brand, so they are loaded for the brand currently selected and reloaded
   * whenever it changes.
   */
  fetchPathFilters() {
    if (!this.campaign) {
      return;
    }

    this.singleStepFilterService
      .list({ campaign: this.campaign.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (filters: SingleStepPathFilter[]) => {
          const pathFilters = {};

          filters.forEach((filter: SingleStepPathFilter) => {
            pathFilters[filter.funnel] = filter;
          });

          this.pathFilters = pathFilters;
        },
        (error) => this.handleError(error)
      );
  }

  /**
   * A path only offers filters once it is assigned to the selected brand - a filter on an unassigned
   * path would be dead config - and Item Status is never filterable, since the portal falls back to
   * it when an item has no other action.
   */
  canFilterPath(path: any): boolean {
    return this.isAssignedToCurrentCampaign(path) && path.type !== OfferIntents.ItemStatus;
  }

  getFilterConditionCount(path: any): number {
    const filter = this.pathFilters[path.id];

    return filter ? getAudienceConditions(filter.audience).length : 0;
  }

  openFiltersPopup(path: any) {
    const filter = this.pathFilters[path.id];

    this.popupTitle = `${path.offerName} Filters`;
    this.popupData = {
      campaign_id: this.campaign.id,
      funnel_id: path.id,
      filter_id: filter ? filter.id : null,
      audience_id: filter ? filter.audience.id : null,
      popupType: AudiencePopup.Conditions,
    };
    this.ngxSmartModalService.getModal('singleStepFiltersPopup').open();
  }

  closeFiltersPopup() {
    this.ngxSmartModalService.getModal('singleStepFiltersPopup').close();
  }

  saveFilters() {
    this.closeFiltersPopup();
    this.fetchPathFilters();
  }

  navigateToStepBuilder(id: number, offerIntent: OfferIntents, firstStep: FirstStep) {
    this.router.navigate(
      ['stage', id, 'intent', offerIntent, 'category', OfferTypes.SingleStep, firstStep.category, 'step', firstStep.id],
      {
        relativeTo: this.route,
      }
    );
  }

  fetchOfferData() {
    this.funnelService.listRetentionOffers(null, null, OfferTypes.SingleStep).subscribe(
      (response: Pager) => {
        this.loader.hide();
        let localPaths = [];
        let templatePaths = [];
        response.results.forEach((item: SingleStep) => {
          const lastModifiedMoment = moment(item.first_step.updated).tz('EST');

          const lastModified = {
            time: lastModifiedMoment.format('HH:mm:ss z'), // "16:18:10 EST"
            date: lastModifiedMoment.format('DD MMM, YYYY'), // "20 Jan, 2025"
          };

          let path = {
            id: item.id,
            displayId: item.display_id,
            offerName: item.name,
            sessions: item.sessions || '--',
            cancels: item.cancels || '--',
            saves: item.saves || '--',
            type: item.offer_intent,
            offerType: item.offer_type,
            lastModified,
            isActive: false,
            isPublishing: false,
            firstStep: item.first_step ? item.first_step : null,
            enabled: item.enabled,
            is_modified: item.is_modified
          };

          if (item.is_public) {
            templatePaths.push(path);
          } else {
            localPaths.push(path);
          }
        });
        this.tableData = localPaths
        this.originalTableData = this.tableData;
        this.templates = templatePaths;
      },
      (error) => {
        this.loader.hide();
        this.handleError(error);
      }
    );
  }

  // Private utility methods for generic sorting
  private genericSortByName(dataArray: any[], sortConfig: StepsSortConfig) {
    if (sortConfig.column === StepSortableColumns.Name) {
      sortConfig.direction =
        sortConfig.direction === TableSortDirection.Asc ? TableSortDirection.Desc : TableSortDirection.Asc;
    } else {
      sortConfig.column = StepSortableColumns.Name;
      sortConfig.direction = TableSortDirection.Asc;
    }

    dataArray.sort((a: any, b: any) => {
      const comparison = a.offerName.localeCompare(b.offerName);
      return sortConfig.direction === TableSortDirection.Asc ? comparison : -comparison;
    });
  }

  private genericSortByLastModified(dataArray: any[], sortConfig: StepsSortConfig) {
    if (sortConfig.column === StepSortableColumns.LastModified) {
      sortConfig.direction =
        sortConfig.direction === TableSortDirection.Asc ? TableSortDirection.Desc : TableSortDirection.Asc;
    } else {
      sortConfig.column = StepSortableColumns.LastModified;
      sortConfig.direction = TableSortDirection.Asc;
    }

    dataArray.sort((a: any, b: any) => {
      const dateA = new Date(`${a.lastModified.date} ${a.lastModified.time}`);
      const dateB = new Date(`${b.lastModified.date} ${b.lastModified.time}`);
      return sortConfig.direction === TableSortDirection.Asc
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    });
  }

  // Public methods for Single Step Paths table
  sortByName() {
    this.genericSortByName(this.tableData, this.currentSort);
  }

  sortByLastModified() {
    this.genericSortByLastModified(this.tableData, this.currentSort);
  }

  // Public methods for Templates table
  sortTemplatesByName() {
    this.genericSortByName(this.templates, this.currentTemplateSort);
  }

  sortTemplatesByLastModified() {
    this.genericSortByLastModified(this.templates, this.currentTemplateSort);
  }

  isNameSorted(): boolean {
    return this.currentSort.column === StepSortableColumns.Name;
  }

  get isNameAsc(): boolean {
    return this.isNameSorted() && this.currentSort.direction === TableSortDirection.Asc;
  }

  get isNameDesc(): boolean {
    return this.isNameSorted() && this.currentSort.direction === TableSortDirection.Desc;
  }

  isDateSorted(): boolean {
    return this.currentSort.column === StepSortableColumns.LastModified;
  }

  get isDateAsc(): boolean {
    return this.isDateSorted() && this.currentSort.direction === TableSortDirection.Asc;
  }

  get isDateDesc(): boolean {
    return this.isDateSorted() && this.currentSort.direction === TableSortDirection.Desc;
  }

  // Getter methods for Templates table sorting state
  isTemplateNameSorted(): boolean {
    return this.currentTemplateSort.column === StepSortableColumns.Name;
  }

  get isTemplateNameAsc(): boolean {
    return this.isTemplateNameSorted() && this.currentTemplateSort.direction === TableSortDirection.Asc;
  }

  get isTemplateNameDesc(): boolean {
    return this.isTemplateNameSorted() && this.currentTemplateSort.direction === TableSortDirection.Desc;
  }

  isTemplateDateSorted(): boolean {
    return this.currentTemplateSort.column === StepSortableColumns.LastModified;
  }

  get isTemplateDateAsc(): boolean {
    return this.isTemplateDateSorted() && this.currentTemplateSort.direction === TableSortDirection.Asc;
  }

  get isTemplateDateDesc(): boolean {
    return this.isTemplateDateSorted() && this.currentTemplateSort.direction === TableSortDirection.Desc;
  }

  cloneTemplate(templateId: number) {
    this.loader.show();
    this.funnelService.clone(templateId).subscribe(
      (response: any) => {
        this.loader.hide();
        this.alertService.success('Cloning single step template.');
      },
      (error) => {
        this.loader.hide();
        this.handleError(error);
      }
    );
  }

  canCustomizeTemplate(templateOfferIntent: OfferIntents): boolean {
    return !this.tableData.some(path => path.type === templateOfferIntent);
  }

  publishPath(path: SingleStep) {
    this.loader.show();
    this.funnelService.publish(path.id).subscribe(
      _ => {
        this.loader.hide();
        this.alertService.success('Publishing single step path.');
        this.tableData = this.tableData.map((p: SingleStep) => {
          if (p.id === path.id) {
            p = {
              ...p,
              is_modified: false,
            };
          }
          return p
        });
      }, error => {
        this.loader.hide();
        this.handleError(error);
      })
  }

  onDelete(id) {
    this.tableData = this.tableData.filter((p: SingleStep) => p.id !== id);
  }

  isAssignedToCurrentCampaign(path: SingleStep): boolean {
    return this.campaignFunnels.includes(path.id);
  }

  toggleCampaignAssignment(event, path: SingleStep) {
    const isAssigned: boolean = event.target.checked;
    let updatedFunnels = [...this.campaignFunnels];

    if (isAssigned) {
      if (!updatedFunnels.includes(path.id)) {
        updatedFunnels.push(path.id);
      } else {
        return
      }
    } else {
      updatedFunnels = updatedFunnels.filter(id => id !== path.id);
    }

    let update = {single_step_funnels: updatedFunnels};
    this.loader.show();
    this.campaignService.patch(this.campaign.id, update)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (updatedCampaign) => {
          this.campaign = updatedCampaign;
          this.campaignFunnels = updatedCampaign['single_step_funnels'] || [];
          this.loader.hide();
          let message = isAssigned ? 'Added path to ' : 'Removed path from ';
          message += this.campaign.display_name;
          this.alertService.success(message);
        },
        error => {
          this.handleError(error);
          this.loader.hide();
        }
      );
  }
}
